/**
 * euro-bundle-guard.js  (loaded globally from theme.liquid)
 *
 * Keeps €1 bundle gift lines honest. Rules, in order:
 *   1. ORPHAN     — a gift line (`_euro_bundle_gift: token`) with no main
 *                   line carrying `_euro_bundle: token` is removed.
 *   2. QTY CLAMP  — a gift line's quantity is forced back to 1.
 *   3. ONE-TIME   — only one gift line per gift PRODUCT per cart; extra
 *                   claims are removed (first one wins).
 *
 * Runs on page load, after every theme cart event, and whenever the cart
 * drawer re-renders (MutationObserver catch-all). Debounced; re-entrancy
 * guarded so its own /cart/change calls don't loop.
 *
 * NOTE: theme-only enforcement. A user hand-crafting /cart/add requests
 * with matching properties could still self-serve the €1 line — accepted
 * trade-off of not using a discount Function.
 */
(function () {
  'use strict';

  let running = false;
  let scheduled = null;

  function schedule() {
    clearTimeout(scheduled);
    scheduled = setTimeout(run, 400);
  }

  async function run() {
    if (running) return;
    running = true;
    try {
      const res = await fetch(`${window.theme.routes.cart_url}.js`, {
        headers: { 'Accept': 'application/json' },
      });
      const cart = await res.json();
      const items = cart.items || [];

      const parentTokens = new Set();
      items.forEach((item) => {
        const t = item.properties && item.properties._euro_bundle;
        if (t) parentTokens.add(t);
      });

      const removals = [];   // line keys -> qty 0
      const clamps = [];     // line keys -> qty 1
      const seenGiftProducts = new Set();

      items.forEach((item) => {
        const token = item.properties && item.properties._euro_bundle_gift;
        if (!token) return;

        // Rule 1: orphan
        if (!parentTokens.has(token)) {
          removals.push(item.key);
          return;
        }
        // Rule 3: one gift per gift product per cart
        if (seenGiftProducts.has(item.product_id)) {
          removals.push(item.key);
          return;
        }
        seenGiftProducts.add(item.product_id);

        // Rule 2: qty clamp
        if (item.quantity > 1) clamps.push(item.key);
      });

      if (!removals.length && !clamps.length) return;

      const sectionsToRender = ['cart-drawer', 'cart-bubble', 'main-cart'];
      let lastState = null;

      for (const key of removals) {
        lastState = await change(key, 0, sectionsToRender);
      }
      for (const key of clamps) {
        lastState = await change(key, 1, sectionsToRender);
      }

      if (lastState) renderCartContents(lastState);
    } catch (e) {
      console.warn('[euro-bundle-guard] check failed', e);
    } finally {
      running = false;
    }
  }

  async function change(key, quantity, sections) {
    const res = await fetch(`${window.theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: key, quantity, sections, sections_url: window.location.pathname }),
    });
    return res.json();
  }

  /** Same render targets as protector-upsell / euro-bundle components. */
  function renderCartContents(parsedState) {
    const sections = parsedState.sections || {};
    const targets = [
      { id: 'Cart', selector: '.thb-cart-form', section: 'main-cart' },
      { id: 'Cart-Drawer', selector: '.cart-drawer', section: 'cart-drawer' },
      { id: 'cart-drawer-toggle', selector: '#cart-drawer-toggle', section: 'cart-bubble' },
    ];
    targets.forEach((t) => {
      const host = document.getElementById(t.id);
      if (!host || !sections[t.section]) return;
      const replace = host.querySelector(t.selector) || host;
      try {
        const html = new DOMParser().parseFromString(sections[t.section], 'text/html');
        const inner = html.querySelector(t.selector);
        if (inner) replace.innerHTML = inner.innerHTML;
      } catch (e) {
        console.warn('[euro-bundle-guard] failed to render', t.section, e);
      }
    });
    const drawer = document.getElementById('Cart-Drawer');
    drawer?.notesToggle?.();
    drawer?.removeProductEvent?.();
  }

  // Triggers
  document.addEventListener('DOMContentLoaded', schedule);
  document.addEventListener('cart:item-added', schedule);
  document.addEventListener('line-item:change:end', schedule);
  document.documentElement.addEventListener('cart:refresh', schedule);

  // Catch-all: any re-render of the cart drawer re-validates the pairing.
  document.addEventListener('DOMContentLoaded', () => {
    const drawer = document.getElementById('Cart-Drawer');
    if (!drawer) return;
    new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
  });
})();
